import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass';
import { SavePass } from 'three/examples/jsm/postprocessing/SavePass';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass';
import { BokehPass } from 'three/examples/jsm/postprocessing/BokehPass';
export const BLOOM = {
    strength: 0.6,
    radius: 0.28,
    /** Only the screens are brighter than this, so only they glow */
    threshold: 0.62,
};
export const DEPTH_OF_FIELD = {
    /** Bigger values blur more per unit of distance from the focal plane */
    aperture: 0.0000045,
    /** Blur ceiling, in screen-space units */
    maxblur: 0.0075,
    /** Extra distance pushed behind the focal point, in world units */
    focusOffset: 900,
};
/**
 * Final pass, doing two jobs the effect stack can't do for itself.
 *
 * Alpha: it is what lets the CSS3D iframe show through the WebGL canvas, and
 * both effects destroy it — bokeh writes alpha 1 over the whole frame, bloom
 * adds alpha wherever it glows. The scene's own alpha is saved off before the
 * effects run and pasted back on here, so the website screen stays a crisp hole
 * while everything around it is blurred and bloomed. The colour is
 * unpremultiplied against that alpha, clamped, and premultiplied again, because
 * bloom can leave pixels brighter than their alpha and browsers composite those
 * as white.
 *
 * Encoding: three forces linear encoding when rendering into a render target,
 * so the renderer's sRGB output never gets applied once a composer is in play.
 * The effects therefore run in linear space (which is where blurring belongs)
 * and the conversion happens once, here, on the way to the canvas.
 */
const ResolveShader = {
    uniforms: {
        tDiffuse: { value: null },
        tAlpha: { value: null },
    },
    vertexShader: /* glsl */ `
        varying vec2 vUv;
        void main() {
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
        }`,
    fragmentShader: /* glsl */ `
        uniform sampler2D tDiffuse;
        uniform sampler2D tAlpha;
        varying vec2 vUv;

        vec3 linearToSRGB( vec3 value ) {
            return mix(
                pow( value, vec3( 0.41666 ) ) * 1.055 - vec3( 0.055 ),
                value * 12.92,
                vec3( lessThanEqual( value, vec3( 0.0031308 ) ) )
            );
        }

        void main() {
            float alpha = texture2D( tAlpha, vUv ).a;
            vec3 premultiplied = texture2D( tDiffuse, vUv ).rgb;

            vec3 color = alpha > 0.0 ? premultiplied / alpha : vec3( 0.0 );
            color = linearToSRGB( clamp( color, 0.0, 1.0 ) );

            gl_FragColor = vec4( color * alpha, alpha );
        }`,
};
/**
 * Re-types an already constructed render target and drops its GPU allocation so
 * the renderer rebuilds it on next use. Needed because the effects render into
 * linear space, and eight bits leave so few levels in the darks that converting
 * back to sRGB at the end posterises every shaded surface.
 */
const useHighPrecision = (target, type) => {
    target.texture.type = type;
    target.dispose();
};
export default class PostProcessing {
    constructor(renderer, scene, camera) {
        const size = renderer.getDrawingBufferSize(new THREE.Vector2());
        // Half float needs a WebGL2 context (or the float buffer extension);
        // without it the effects still run, just with more banding.
        const bufferType = renderer.capabilities.isWebGL2
            ? THREE.HalfFloatType
            : THREE.UnsignedByteType;
        this.composer = new EffectComposer(renderer, new THREE.WebGLRenderTarget(size.width, size.height, {
            minFilter: THREE.LinearFilter,
            magFilter: THREE.LinearFilter,
            format: THREE.RGBAFormat,
            type: bufferType,
        }));
        this.composer.setPixelRatio(renderer.getPixelRatio());
        this.alphaTarget = new THREE.WebGLRenderTarget(size.width, size.height, {
            minFilter: THREE.NearestFilter,
            magFilter: THREE.NearestFilter,
            format: THREE.RGBAFormat,
            type: THREE.UnsignedByteType,
            depthBuffer: false,
        });
        this.alphaTarget.texture.name = 'PostProcessing.alpha';
        this.renderPass = new RenderPass(scene, camera);
        this.savePass = new SavePass(this.alphaTarget);
        // SavePass blends by default, which would composite the alpha we are
        // trying to capture against whatever was in the target last frame.
        this.savePass.material.blending =
            THREE.NoBlending;
        this.savePass.clear = true;
        this.bloomPass = new UnrealBloomPass(new THREE.Vector2(size.width, size.height), BLOOM.strength, BLOOM.radius, BLOOM.threshold);
        // UnrealBloomPass builds its own eight bit mip chain, which bands in
        // exactly the same way as the main buffer would.
        useHighPrecision(this.bloomPass.renderTargetBright, bufferType);
        this.bloomPass.renderTargetsHorizontal.forEach((target) => useHighPrecision(target, bufferType));
        this.bloomPass.renderTargetsVertical.forEach((target) => useHighPrecision(target, bufferType));
        this.bokehPass = new BokehPass(scene, camera, {
            focus: 1,
            aperture: DEPTH_OF_FIELD.aperture,
            maxblur: DEPTH_OF_FIELD.maxblur,
            width: size.width,
            height: size.height,
        });
        // BokehPass renders into the write buffer but ships with needsSwap
        // false, which is only correct when it is the last pass. It isn't here.
        this.bokehPass.needsSwap = true;
        this.bokehUniforms = this.bokehPass.uniforms;
        this.resolvePass = new ShaderPass(ResolveShader);
        this.resolvePass.material.blending = THREE.NoBlending;
        this.resolvePass.uniforms.tAlpha.value = this.alphaTarget.texture;
        // Individually switchable so the stack can be bisected in the browser
        const params = new URLSearchParams(window.location.search);
        this.bloomPass.enabled = !params.has('nobloom');
        this.bokehPass.enabled = !params.has('nodof');
        this.composer.addPass(this.renderPass);
        this.composer.addPass(this.savePass);
        this.composer.addPass(this.bloomPass);
        this.composer.addPass(this.bokehPass);
        this.composer.addPass(this.resolvePass);
    }
    /**
     * Keeps the focal plane on whatever the camera is looking at, so the
     * website monitor stays sharp while you are reading it and the pile falls
     * away in front of and behind it.
     */
    setFocalDistance(distance) {
        this.bokehUniforms.focus.value =
            distance + DEPTH_OF_FIELD.focusOffset;
    }
    setSize(width, height, pixelRatio) {
        this.composer.setPixelRatio(pixelRatio);
        this.composer.setSize(width, height);
        const bufferWidth = Math.floor(width * pixelRatio);
        const bufferHeight = Math.floor(height * pixelRatio);
        this.alphaTarget.setSize(bufferWidth, bufferHeight);
        this.bokehPass.renderTargetDepth.setSize(bufferWidth, bufferHeight);
        this.bokehUniforms.aspect.value = width / height;
    }
    render() {
        this.composer.render();
    }
}
